import { requestRecordingPermissionsAsync } from 'expo-audio';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Animated,
  Dimensions,
  FlatList,
  Platform,
  ScrollView,
  StyleSheet,
  TextInput,
  useWindowDimensions,
} from 'react-native';
import { useMentions } from 'react-native-controlled-mentions';
import { MentionInput } from 'react-native-controlled-mentions-web';
import uuid from 'react-native-uuid';

import { Alert } from '@components/Alert';
import ChatImages from '@components/Chat/ChatImages';
import { ChatInputOptionsBar } from '@components/Chat/ChatInputOptionsBar';
import DocumentCard from '@components/Chat/DocumentCard';
import MentionSuggestions, {
  EveryoneType,
} from '@components/Chat/MentionSuggestions';
import { ReplyMessage } from '@components/Chat/ReplyMessage';
import TagsCollection from '@components/Chat/TagsCollection';
import { VoiceCard } from '@components/Chat/VoiceCard';
import { VoiceRecord } from '@components/Chat/VoiceRecord';
import { HoverableScrollView } from '@components/HoverableScrollView';
import { Box, Text } from '@components/Restyle/index';
import Icon from '@components/shared/Icon/Icon';
import { TooltipOnboardingWeb } from '@components/Web/TooltipOnboarding.web';
import { Chat, LocalFile, User } from '@graphql/generated';
import useChatInput from '@hooks/useChatInput';
import { useChatTutorialWeb } from '@hooks/useChatTutorialWeb';
import { useChatUploadManager } from '@hooks/useChatUploadManager';
import useEmoji from '@hooks/useEmoji';
import { useOutsideClick } from '@hooks/useOutsideClick.web';
import { usePreviousValue } from '@hooks/usePreviousValue';
import useSearch from '@hooks/useSearch';
import theme from '@themes/theme';
import { getClipboardImage, hasClipboardImage } from '@utils/clipboardImage';
import { triggersConfig } from '@utils/mentions';
import { AsyncStorage } from '@utils/storage';
import { canUploadFileToTeam } from '@utils/teams';

export const handleTagPressNew = (
  currentInputKey: React.RefObject<string>,
  lastChar: (string: string) => string,
  message: string,
  isOneMoreHashInCurrent: boolean,
  setMessage: (message: string) => void,
  setIsCompletingTask: (value: React.SetStateAction<boolean>) => void,
  setIsTagModalOpen: (value: React.SetStateAction<boolean>) => void
) => {
  if (
    currentInputKey.current === 'Meta' ||
    currentInputKey.current === 'Control' ||
    currentInputKey.current === 'Paste'
  )
    return;

  if (lastChar(message) === '#' || isOneMoreHashInCurrent) {
    // remove the #
    setMessage(message.slice(0, -1));
  }
  setIsCompletingTask(false);
  setIsTagModalOpen(true);
};

export const onKeyPressForCtrlV = async (
  e: { key: string },
  currentInputKey: { current: string },
  setHasImage: (value: boolean) => void,
  checkImageFromClipboard: () => Promise<void>
) => {
  if (
    e.key === 'Meta' ||
    e.key === 'Control' ||
    e.key.toLocaleLowerCase() === 'v'
  ) {
    if (
      (currentInputKey.current === 'Meta' ||
        currentInputKey.current === 'Control') &&
      e.key.toLocaleLowerCase() === 'v'
    ) {
      setHasImage(false);
      await checkImageFromClipboard();
    }
  }
  currentInputKey.current = e.key;
};

/**
 * Returns `true` if the `element` is of the type `HTMLTextAreaElement`.
 *
 * This is a shallow check against the `element.style` and `element.scrollHeight` properties.
 * @param element The element to check
 * @returns `true` if the element is of type `HTMLTextAreaElement`
 */
const isHtmlTextAreaElement = (
  element?: TextInput | HTMLTextAreaElement | null
): element is HTMLTextAreaElement => {
  return !!element && 'style' in element && 'scrollHeight' in element;
};

type ChatFormProps = {
  onSend: (message1?: string) => void;
  isGroupChat?: boolean;
  chatUsers: User[];
  chatId?: Chat['id'];
  isBlocked?: boolean;
  disableInput?: boolean;
  isPendingRequest?: boolean;
  showLeaveMessage?: boolean | string;
  onFirstActivity?: () => void;
  highlightTagButton?: boolean;
  onHighlightTagPress?: () => void;
  autoFocusInput?: boolean;
  showTagOnboardingTooltip?: boolean;
  onTagOnboardingTryIt?: () => void;
  showStep3Tooltip?: boolean;
  step3TooltipAnimatedOpacity?: Animated.Value;
  onStep3TooltipActivity?: () => void;
};

const PLACEHOLDER = 'Type message here...';

// TA-10279: Send must lock while any file *currently attached to this
// composer's own draft* is still compressing/uploading — scoped to this
// chat's `localFiles` (by clientId) so an unrelated background upload in a
// different chat can never block Send here. Extracted as a pure helper so
// the contract is directly unit-testable.
export const hasPendingUploads = (
  uploadingFiles: { file: { clientId: string }; status?: string }[],
  localFiles: { clientId: string }[]
): boolean => {
  const localClientIds = new Set(localFiles.map((f) => f.clientId));
  return uploadingFiles.some(
    (f) =>
      localClientIds.has(f.file.clientId) &&
      // Anything other than a settled success/error is still pending —
      // covers the queued-but-not-yet-started case (status not yet set)
      // the same way ChatImages.tsx's own `!status` loading branch does.
      f.status !== 'success' &&
      f.status !== 'error'
  );
};

const ChatForm: React.FC<ChatFormProps> = ({
  onSend,
  isGroupChat,
  chatUsers,
  chatId,
  isBlocked,
  disableInput,
  showLeaveMessage,
  isPendingRequest,
  onFirstActivity,
  highlightTagButton,
  onHighlightTagPress,
  autoFocusInput = false,
  showTagOnboardingTooltip,
  onTagOnboardingTryIt,
  showStep3Tooltip,
  step3TooltipAnimatedOpacity,
  onStep3TooltipActivity,
}) => {
  const { t } = useTranslation();
  const dimensions = useWindowDimensions();
  const [scrollEnabled, setScrollEnabled] = useState<boolean>(false);
  const {
    isEmojiPickerOpened,
    toggleEmojiPickerOpened,
    setFromValue,
    showMessageEmojiModal,
    setShowMessageEmojiModal,
  } = useEmoji();
  const [currentUserIndex, setCurrentUserIndex] = useState(0);
  const currentInputKey = useRef<string>('');
  const firstActivityFired = useRef(false);
  const step3ActivityFired = useRef(false);

  useEffect(() => {
    step3ActivityFired.current = false;
  }, [showStep3Tooltip]);

  useEffect(() => {
    firstActivityFired.current = false;
  }, [chatId]);

  const [filteredChatUsers, setFilteredChatUsers] = useState<User[]>(chatUsers);
  const [filteredChatUsersKeyword, setFilteredChatUsersKeyword] = useState();
  const [isShowUsers, setIsShowUsers] = useState<boolean>(false);
  const [hitEnterOrTabToSelectUser, setHitEnterOrTabToSelectUser] =
    useState(false);
  const [hasImage, setHasImage] = useState<boolean>(false);

  const {
    message,
    setMessage,
    setIsTagModalOpen,
    setIsCompletingTask,
    getTagsCollection,
    setTagsCollection,
    removeTagsCollection,
    removeTagsCollectionTask,
    getLocalFiles,
    setLocalFiles,
    getReplyMessage,
    setReplyMessage,
    pickChatImages,
    launchChatCamera,
    pickChatFiles,
    focus,
    setFocus,
    shouldShowTutorialHashTag,
    setShowChatOptionsBar,
    setIsAssigningTask,
    setIsAssigningTaskStep2,
    messageSelectionRef,
    setAssignTaskSourceCard,
  } = useChatInput();

  const {
    isVisibleForChat,
    showOverlayForChat,
    isFromChatList,
    chatTutorialWebState,
    setChatTutorialWebStateAndInStorage,
  } = useChatTutorialWeb();

  const isShowChatTutorial = isVisibleForChat;

  const [message1, setMessage1] = useState<string>('');

  const saveMessageToLocal = async (value: string) => {
    await AsyncStorage.setItem('message', value);
  };

  useEffect(() => {
    if (message && isShowChatTutorial && message1 !== message) {
      setMessage1(message1 + message);
      saveMessageToLocal(message1 + message);
      setMessage('');
    }
  }, [message]);

  const tagsCollection = chatId ? getTagsCollection(chatId) : [];
  const localFiles = chatId ? getLocalFiles(chatId) : [];
  const replyMessage = chatId ? getReplyMessage(chatId) : undefined;
  const { removeUploadingFile, uploadingFiles } = useChatUploadManager();

  useEffect(() => {
    setTimeout(() => {
      if (
        tagsCollection.length === 0 &&
        chatTutorialWebState === (isFromChatList ? '7' : '6')
      ) {
        setChatTutorialWebStateAndInStorage(isFromChatList ? '6' : '5');
      }
      isShowChatTutorial && getMessage();
    }, 500);
  }, [tagsCollection]);

  const getMessage = async () => {
    const msg = await AsyncStorage.getItem('message');
    if (msg && msg.length > 0) {
      setMessage1(msg);
      setMessage('');
    }
  };

  const [disableUpload, setDisableUpload] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const { isSearching } = useSearch();
  const ref_inputBox = useRef<TextInput>(null);
  // Programmatic focus on web should leave the caret after the existing text
  // so the user can keep typing (TA-10892 #28); a bare focus() restores the
  // browser's last selection, which is often the start of the textarea.
  const focusInputAtEnd = useCallback(() => {
    const input = ref_inputBox.current as
      | (TextInput & {
          value?: string;
          setSelectionRange?: (start: number, end: number) => void;
        })
      | null;
    input?.focus();
    if (Platform.OS === 'web' && input?.setSelectionRange) {
      const end = input.value?.length ?? 0;
      input.setSelectionRange(end, end);
    }
  }, []);

  const focusInput = useCallback(() => {
    if (Platform.OS === 'web') {
      setTimeout(focusInputAtEnd, 1);
      return;
    }
    focusInputAtEnd();
  }, [focusInputAtEnd]);

  useEffect(() => {
    // Skip when the input already has focus: autoFocusInput mirrors `focus`,
    // so a programmatic focus (e.g. restoring the caret after the emoji
    // picker, TA-11126) would otherwise be followed by a jump to the end.
    if (!autoFocusInput || ref_inputBox.current?.isFocused?.()) return;
    focusInput();
  }, [autoFocusInput]);

  const wasEmojiPickerOpened = useRef(false);

  // Snapshot the caret before focus leaves the textarea (e.g. to the emoji
  // picker) so the picker can insert at it instead of appending (TA-11126).
  // Blurs while the picker is open are skipped so they cannot overwrite the
  // caret captured when the picker was opened.
  const captureSelection = useCallback(() => {
    if (wasEmojiPickerOpened.current) return;
    const input = ref_inputBox.current as
      | (TextInput & { selectionStart?: number; selectionEnd?: number })
      | null;
    if (typeof input?.selectionStart !== 'number') return;
    messageSelectionRef.current = {
      start: input.selectionStart,
      end: input.selectionEnd ?? input.selectionStart,
    };
  }, [messageSelectionRef]);

  // onBlur re-focuses the composer while `focus` is set (TA-10892). Put the
  // caret back where the user left it rather than at the end, otherwise any
  // blur (e.g. the mouse leaving the composer) jumps the caret (TA-11126).
  const refocusKeepingCaret = useCallback(() => {
    const selection = messageSelectionRef.current;
    if (!selection) {
      focusInputAtEnd();
      return;
    }
    const input = ref_inputBox.current as
      | (TextInput & {
          setSelectionRange?: (start: number, end: number) => void;
        })
      | null;
    input?.focus();
    input?.setSelectionRange?.(selection.start, selection.end);
  }, [focusInputAtEnd, messageSelectionRef]);

  // Once the picker closes, put the caret back just after the inserted emoji
  // (or where it was, if the picker was dismissed without a pick).
  useEffect(() => {
    const justClosed = wasEmojiPickerOpened.current && !isEmojiPickerOpened;
    wasEmojiPickerOpened.current = isEmojiPickerOpened;
    if (!justClosed || Platform.OS !== 'web') return;
    const selection = messageSelectionRef.current;
    if (!selection) return;
    setTimeout(() => {
      const input = ref_inputBox.current as
        | (TextInput & {
            setSelectionRange?: (start: number, end: number) => void;
          })
        | null;
      input?.focus();
      input?.setSelectionRange?.(selection.start, selection.end);
    }, 1);
  }, [isEmojiPickerOpened]);

  const handleOutsideClick = () => {
    setTimeout(() => ref_inputBox.current?.blur(), 1);
    setFocus(false);
  };
  const ref = useOutsideClick(handleOutsideClick);

  const { textInputProps } = useMentions({
    value: isShowChatTutorial ? message1 : message,
    onChange: isShowChatTutorial ? setMessage1 : setMessage,
    triggersConfig,
  });

  const lastChar = (string: string) => {
    return string.charAt(string.length - 1);
  };

  const handleTagPress = async () => {
    showMessageEmojiModal && setShowMessageEmojiModal(false);
    if (
      isVisibleForChat &&
      showOverlayForChat &&
      chatTutorialWebState === (isFromChatList ? '7' : '6')
    ) {
      setChatTutorialWebStateAndInStorage(isFromChatList ? '6' : '5');
    }

    if (message1 && message1.length > 0) {
      setMessage(message1);
      await AsyncStorage.setItem('message', message1);
    }

    handleTagPressNew(
      currentInputKey,
      lastChar,
      isShowChatTutorial ? message1 : message,
      isOneMoreHashInCurrent,
      isShowChatTutorial ? setMessage1 : setMessage,
      setIsCompletingTask,
      setIsTagModalOpen
    );
  };

  useEffect(() => {
    isVisibleForChat &&
      showOverlayForChat &&
      chatTutorialWebState === (isFromChatList ? '6' : '5') &&
      handleTagPress();
  }, [chatTutorialWebState]);

  useEffect(() => {
    if (isShowChatTutorial && focus) {
      focusInputAtEnd();
    } else if (
      (chatTutorialWebState === '2' ||
        chatTutorialWebState === (isFromChatList ? '8' : '7')) &&
      !focus
    ) {
      ref_inputBox.current?.blur();
    }
  }, [focus]);

  useEffect(() => {
    if (ref_inputBox.current) {
      const inputEl = ref_inputBox.current;
      inputEl.addEventListener('paste', () => {
        currentInputKey.current = 'Paste';
      });
    }
  }, []);

  useEffect(() => {
    const hasExpiredTag = tagsCollection?.some((item) =>
      canUploadFileToTeam(item.project.team)
    );

    setDisableUpload(hasExpiredTag);
  }, [tagsCollection.length]);

  useEffect(() => {
    const hasExpiredTag = tagsCollection?.some(
      (item) =>
        (item.project?.team?.subscriptionDetails &&
          new Date(
            item.project?.team?.subscriptionDetails?.expirationDate
          ).getTime() < new Date().getTime()) ||
        item.project?.team?.subscriptionDetails?.isSubscriptionCanceled
    );
    setIsExpired(hasExpiredTag);
  }, [tagsCollection?.length]);

  const [hover, setHover] = useState(false);
  // Event handlers require the exact method instance to add and remove
  const mouseOver = useCallback(() => setHover(true), [setHover]);
  const mouseOut = useCallback(() => setHover(false), [setHover]);

  const prevMessage = usePreviousValue({
    message: isShowChatTutorial ? message1 : message,
  });
  const prevHashCount = (prevMessage?.message.match(/#/g) || []).length;
  const currentHashCount = (
    (isShowChatTutorial ? message1 : message).match(/#/g) || []
  ).length;
  const isOneMoreHashInCurrent = currentHashCount === prevHashCount + 1;

  useEffect(() => {
    const lastCharOfMessage = lastChar(isShowChatTutorial ? message1 : message);

    // typed a '#', open the modal do not open if '#' present from a draft (prevMessage undefined)
    if (prevMessage && (lastCharOfMessage === '#' || isOneMoreHashInCurrent)) {
      handleTagPress();
    }

    if (Platform.OS === 'web' && isHtmlTextAreaElement(ref_inputBox.current)) {
      // We have to restrict overflow so the scrollbar does not interfere with calculations.
      ref_inputBox.current.style.overflow = 'hidden';

      // Here we have to reset the height before we can check the scroll height again.This allows us to account for size changes in both directions.
      ref_inputBox.current.style.height = 'auto';

      // To expand the input, we have to force the height to be the same as the scroll height.
      ref_inputBox.current.style.height =
        ref_inputBox.current.scrollHeight + 'px';

      // Reset the overflow so we can scroll again.
      ref_inputBox.current.style.overflow = 'auto';
    }
  }, [message, message1, ref_inputBox.current]);

  // Hover overflow has to be separated from height calculations
  useEffect(() => {
    if (Platform.OS === 'web' && isHtmlTextAreaElement(ref_inputBox.current)) {
      // Handle mouse events to track hover state
      ref_inputBox.current.addEventListener('mouseover', mouseOver);
      ref_inputBox.current.addEventListener('mouseout', mouseOut);

      // Only allow scroll on hover
      ref_inputBox.current.style.overflow = hover ? 'auto' : 'hidden';
    }

    return () => {
      if (
        Platform.OS === 'web' &&
        isHtmlTextAreaElement(ref_inputBox.current)
      ) {
        ref_inputBox.current.removeEventListener('mouseover', mouseOver);
        ref_inputBox.current.removeEventListener('mouseout', mouseOut);
      }
    };
  }, [hover, ref_inputBox.current]);

  useEffect(() => {
    // auto open tag modal on focus if last char '#'
    if (focus && lastChar(isShowChatTutorial ? message1 : message) === '#') {
      setIsCompletingTask(false);
      setIsTagModalOpen(true);
    }
  }, [focus]);

  const audioMessages = localFiles.filter((item) => item.isAudio);
  const images = localFiles.filter((item) => item.isImage);
  const documents = localFiles.filter((item) => !item.isImage && !item.isAudio);
  const showOptionsBar = true;

  useEffect(() => {
    setShowChatOptionsBar(showOptionsBar);
  }, [showOptionsBar]);

  const disableSend =
    (!(isShowChatTutorial ? message1 : message).trim().length &&
      ((tagsCollection != undefined &&
        tagsCollection?.length > 0 &&
        !(isShowChatTutorial ? message1 : message).trim().length) ||
        !tagsCollection?.length) &&
      !localFiles?.length) ||
    disableUpload ||
    isExpired ||
    // TA-10279: lock Send until this draft's own attachments finish
    // compressing/uploading — previously Send was clickable at 5%
    // uploaded, and the message just sat as a draft bubble while the
    // background queue (useChatProcessing's waitUntilNotUploading) waited
    // up to 10 minutes with no user-visible feedback.
    hasPendingUploads(uploadingFiles, localFiles);

  const sendMessage = async () => {
    if (disableSend || !chatId) {
      focus && focusInput();
      return;
    }
    onSend(isShowChatTutorial ? message1 : undefined);
    let shouldClear = false;
    if (!shouldShowTutorialHashTag) {
      shouldClear = true;
    } else {
      const tutorial = await AsyncStorage.getItem('@tutoralizationHashtag');
      if (tutorial === '1') {
        shouldClear = true;
      }
    }
    if (shouldClear) {
      // clear attachments
      setLocalFiles(chatId, []);
      setTagsCollection(chatId, []);
      isShowChatTutorial ? setMessage1('') : setMessage('');
      isShowChatTutorial && (await AsyncStorage.setItem('message', ''));
      setReplyMessage(chatId, undefined);
      focusInput();
    }
  };

  const [showRecording, setShowRecording] = useState(false);

  const showRecordingBox = async () => {
    showMessageEmojiModal && setShowMessageEmojiModal(false);
    if (audioMessages.length > 0) {
      Alert.alert(t('shared:alert'), t('models:chat.input.maxAudio'));
      return;
    }
    if (images.length > 0) {
      Alert.alert(
        t('shared:alert'),
        t('models:chat.input.mediaConflictImagesAudio')
      );
      return;
    }
    if (documents.length > 0) {
      Alert.alert(
        t('shared:alert'),
        t('models:chat.input.mediaConflictFilesAudio')
      );
      return;
    }
    try {
      if (Platform.OS === 'web') {
        setShowRecording(true);
      } else {
        const audioRP = await requestRecordingPermissionsAsync();
        if (audioRP.status === 'granted') {
          setShowRecording(true);
        } else {
          Alert.alert(
            t('shared:allowAudioPermission'),
            t('models:chat.audioPermission')
          );
        }
      }
    } catch (err) {
      console.error('Requesting permissions', err);
    }
  };

  const openEmojiModal = () => {
    showMessageEmojiModal && setShowMessageEmojiModal(false);
    setFromValue('webchat-input');
    toggleEmojiPickerOpened(true);
  };

  useEffect(() => {
    const textarea = ref_inputBox.current;
    if (textarea) {
      const handlePaste = () => {
        setHasImage(false);
        checkImageFromClipboard();
      };

      textarea.addEventListener('paste', handlePaste);
      return () => textarea.removeEventListener('paste', handlePaste);
    }
  }, [ref_inputBox.current]);

  useEffect(() => {
    if (hasImage) {
      getImageFromClipboard();
      setHasImage(false);
    }
  }, [hasImage]);

  const removeLocalFile = async (file: LocalFile) => {
    setLocalFiles(
      chatId!,
      localFiles.filter((item) => item.id !== file.id)
    );
    removeUploadingFile(file);
  };

  const getImageFromClipboard = async () => {
    const img = await getClipboardImage({
      format: 'png',
    });
    if (img) {
      const clientId = uuid.v4().toString();
      const localFile = {
        isImage: true,
        contentType: 'image/png',
        isPreviewable: true,
        thumbnail: '',
        name: clientId + '.png',
        url: img?.data,
        size: img?.data.length,
        clientId,
        id: clientId,
        cdnBaseUrl: '',
        path: '',
        isAudio: false,
        __typename: 'LocalFile',
      } as LocalFile;
      chatId && setLocalFiles(chatId, [...localFiles, localFile]);
    }
  };

  const checkImageFromClipboard = async () => {
    const hasImage = await hasClipboardImage();
    setHasImage(hasImage);
  };

  if (isSearching || !chatId || disableInput) return null;

  const optionsBar = (
    <ChatInputOptionsBar
      onMicSelect={showRecordingBox}
      onTagPress={handleTagPress}
      onPlusSelect={() => {
        showMessageEmojiModal && setShowMessageEmojiModal(false);
        setFocus(false);
        setIsTagModalOpen(false);
      }}
      onCompletedTask={() => {
        showMessageEmojiModal && setShowMessageEmojiModal(false);
        setIsTagModalOpen(false);
        setIsCompletingTask(true);
      }}
      onAssignTask={() => {
        showMessageEmojiModal && setShowMessageEmojiModal(false);
        setIsTagModalOpen(false);
        setIsAssigningTaskStep2(false);
        setIsCompletingTask(false);
        // Not opened from a MEMBER JOINED card — drop any earlier card's.
        setAssignTaskSourceCard(undefined);
        setIsAssigningTask(true);
      }}
      onImagePress={() => {
        showMessageEmojiModal && setShowMessageEmojiModal(false);
        pickChatImages(chatId);
        if (!firstActivityFired.current) {
          firstActivityFired.current = true;
          onFirstActivity?.();
        }
      }}
      onCameraPress={() => {
        showMessageEmojiModal && setShowMessageEmojiModal(false);
        launchChatCamera(chatId);
        if (!firstActivityFired.current) {
          firstActivityFired.current = true;
          onFirstActivity?.();
        }
      }}
      onDocumentPress={() => {
        showMessageEmojiModal && setShowMessageEmojiModal(false);
        pickChatFiles(chatId);
        if (!firstActivityFired.current) {
          firstActivityFired.current = true;
          onFirstActivity?.();
        }
      }}
      onEmojiPress={openEmojiModal}
      onSend={() => {
        showMessageEmojiModal && setShowMessageEmojiModal(false);
        sendMessage();
      }}
      disableSend={disableSend}
      chatId={chatId}
      focus={focus}
      highlightTagButton={highlightTagButton}
      onHighlightTagPress={onHighlightTagPress}
      showTagOnboardingTooltip={showTagOnboardingTooltip}
      onTagOnboardingTryIt={onTagOnboardingTryIt}
    />
  );

  const onChangeText = async (value: string) => {
    isShowChatTutorial ? setMessage1(value) : setMessage(value);
    isShowChatTutorial && (await AsyncStorage.setItem('message', value));
    ref_inputBox.current?.focus();
    if (value.length > 0 && !firstActivityFired.current) {
      firstActivityFired.current = true;
      onFirstActivity?.();
    }
    if (showStep3Tooltip && value.length > 0 && !step3ActivityFired.current) {
      step3ActivityFired.current = true;
      onStep3TooltipActivity?.();
    }
  };

  const renderSuggestions =
    () =>
    ({ keyword, onSuggestionPress }) => {
      if (keyword == null) {
        return null;
      }
      if (Platform.OS === 'web' && filteredChatUsersKeyword !== keyword) {
        setFilteredChatUsersKeyword(keyword);
        setCurrentUserIndex(0);
      }
      Platform.OS === 'web' && !isShowUsers && setIsShowUsers(true);
      return (
        <Box zIndex={1} position='absolute' bottom={0}>
          <MentionSuggestions
            isGroupChat={isGroupChat}
            onSuggestionPress={(item: User | EveryoneType) => {
              isShowUsers && setIsShowUsers(false);
              onSuggestionPress?.(item);
            }}
            keyword={keyword}
            chatUsers={chatUsers}
            isShowUsers={isShowUsers}
            setIsShowUsers={Platform.OS === 'web' ? setIsShowUsers : undefined}
            hitEnterOrTabToSelectUser={hitEnterOrTabToSelectUser}
            setHitEnterOrTabToSelectUser={
              Platform.OS === 'web' ? setHitEnterOrTabToSelectUser : undefined
            }
            currentUserIndex={currentUserIndex}
            setCurrentUserIndex={
              Platform.OS === 'web' ? setCurrentUserIndex : undefined
            }
            setFilteredChatUsers={
              Platform.OS === 'web' ? setFilteredChatUsers : undefined
            }
          />
        </Box>
      );
    };

  const renderMentionSuggestions = renderSuggestions();

  return (
    <Box
      position='relative'
      mx='m'
      mb='m'
      onMouseLeave={() => {
        if (Platform.OS === 'web') {
          ref_inputBox.current?.blur();
          setFocus(false);
        }
      }}
      onWheel={() => {
        if (Platform.OS === 'web') {
          ref_inputBox.current?.blur();
          setFocus(false);
        }
      }}>
      {!disableInput && !showLeaveMessage && !isBlocked && (
        <Box
          ref={ref}
          backgroundColor='white'
          borderRadius='m'
          borderWidth={1}
          borderColor={focus ? 'grey04' : 'grey02'}
          zIndex={2}
          style={
            Platform.OS !== 'web' && [
              focus && styles.topShadow,
              {
                marginBottom: -theme.spacing.m,
                paddingBottom: theme.spacing.m,
              },
            ]
          }>
          {!showRecording && (
            <HoverableScrollView
              keyboardShouldPersistTaps='always'
              onContentSizeChange={(_w, h) => setScrollEnabled(h > 200)}
              scrollEnabled={Platform.OS !== 'web' && scrollEnabled}
              style={{
                maxHeight: Platform.OS !== 'web' ? 200 : undefined,
                marginBottom: theme.spacing.s,
                ...Platform.select({
                  web: {
                    overflow: 'visible',
                    margin: theme.spacing.m,
                  },
                }),
              }}>
              {replyMessage && (
                <Box mb='xs'>
                  <ReplyMessage
                    message={replyMessage}
                    onDismiss={() => setReplyMessage(chatId, undefined)}
                  />
                </Box>
              )}

              <HoverableScrollView
                keyboardShouldPersistTaps='always'
                scrollEnabled={Platform.OS === 'web'}
                style={Platform.select({
                  web: {
                    maxHeight: 500,
                  },
                })}>
                {images.length > 0 && (
                  <Box accessibilityLabel='Image draft' mb='xs'>
                    <ChatImages onDelete={removeLocalFile} list={images} />
                  </Box>
                )}

                {audioMessages.length > 0 && (
                  <Box marginHorizontal='m'>
                    <VoiceCard
                      voice={{ ...audioMessages[0] }}
                      onDelete={() => {
                        removeLocalFile(audioMessages[0]);
                      }}
                    />
                  </Box>
                )}

                {documents.length > 0 && (
                  <FlatList
                    style={{
                      marginBottom: theme.spacing.xs,
                    }}
                    keyboardShouldPersistTaps='always'
                    data={documents}
                    horizontal={Platform.OS !== 'web'}
                    scrollEnabled={
                      Platform.OS !== 'web' && documents.length > 1
                    }
                    keyExtractor={(item) => item.id}
                    renderItem={({ item }) => (
                      <Box
                        style={{
                          width:
                            Platform.OS !== 'web'
                              ? dimensions.width - 92
                              : undefined,
                          marginTop:
                            Platform.OS === 'web'
                              ? theme.spacing.xxs
                              : undefined,
                        }}>
                        <DocumentCard
                          document={{ ...item }}
                          onDelete={() => removeLocalFile(item)}
                        />
                      </Box>
                    )}
                    ItemSeparatorComponent={() => <Box marginRight='xxs' />}
                    ListFooterComponent={() => <Box marginRight='xs' />}
                    ListHeaderComponent={() => <Box marginLeft='m' />}
                  />
                )}

                {tagsCollection?.map((coll, index) => {
                  const { project } = coll;
                  const tagEl = (
                    <Box
                      accessibilityLabel='Tag draft'
                      key={project?.id}
                      mb='xs'>
                      <TagsCollection
                        tagsCollection={coll}
                        showAuthor={false}
                        onDeleteProject={() => {
                          if (tagsCollection[index].tasks.length === 0)
                            removeTagsCollection(chatId, index);
                        }}
                        onDelete={(i: number) =>
                          removeTagsCollectionTask(chatId, i, index)
                        }
                      />
                    </Box>
                  );

                  if (
                    Platform.OS === 'web' &&
                    index === 0 &&
                    showStep3Tooltip
                  ) {
                    return (
                      <TooltipOnboardingWeb
                        key={project?.id}
                        variant='left-center'
                        tooltipStyle='success'
                        title={t('models:chat.onboarding.hashTagStep3.title')}
                        description={t(
                          'models:chat.onboarding.hashTagStep3.description'
                        )}
                        step={t('models:chat.onboarding.hashTagStep3.step')}
                        forceShow={true}
                        disabled={false}
                        trigger='press'
                        offset={theme.spacing.xl}
                        animatedOpacity={step3TooltipAnimatedOpacity}>
                        {tagEl}
                      </TooltipOnboardingWeb>
                    );
                  }
                  return tagEl;
                })}
              </HoverableScrollView>

              <Box
                accessibilityLabel='Message draft'
                flexDirection='row'
                alignItems={Platform.select({
                  default: 'flex-start',
                  web: 'center',
                })}
                {...Platform.select({
                  default: {
                    paddingTop: 's',
                    paddingBottom: 'xs',
                    paddingHorizontal: 'xs',
                  },
                  web: {},
                })}>
                <Box flex={1} marginBottom='s'>
                  {isExpired || disableUpload ? (
                    <>
                      <Box flexDirection='row' alignItems='center'>
                        <Icon
                          name='Info'
                          color='alertRed'
                          height={16}
                          width={16}
                          marginEnd='xxs'
                        />
                        <Text variant='metadata' color='alertRed'>
                          The project's team plan has expired
                        </Text>
                      </Box>
                    </>
                  ) : null}
                  <Box marginTop='m'>
                    <MentionInput
                      zIndex={99999}
                      inputRef={ref_inputBox}
                      value={isShowChatTutorial ? message1 : message}
                      onFocus={() => setFocus(true)}
                      onChange={onChangeText}
                      placeholderTextColor={theme.colors.grey04}
                      placeholder={PLACEHOLDER}
                      onKeyPress={async (
                        e: React.KeyboardEvent<HTMLInputElement>
                      ) => {
                        if (Platform.OS === 'web') {
                          onKeyPressForCtrlV(
                            e,
                            currentInputKey,
                            setHasImage,
                            checkImageFromClipboard
                          );
                          if (
                            isShowUsers &&
                            (e.key === 'Enter' || e.key === 'Tab')
                          ) {
                            setHitEnterOrTabToSelectUser(true);
                            ref_inputBox.current?.focus();
                            e.preventDefault();
                            return;
                          }
                          if (isShowUsers && e.key === 'ArrowUp') {
                            currentUserIndex > 0 &&
                              setCurrentUserIndex(currentUserIndex - 1);
                            e.preventDefault();
                            return;
                          }
                          if (isShowUsers && e.key === 'ArrowDown') {
                            currentUserIndex < filteredChatUsers.length - 1 &&
                              setCurrentUserIndex(currentUserIndex + 1);
                            e.preventDefault();
                            return;
                          }
                          isShowUsers && setIsShowUsers(false);
                        }
                      }}
                      style={styles.input}
                      {...Platform.select({
                        web: {
                          onSubmitEditing: () => {
                            sendMessage();
                          },
                          onBlur: () => {
                            captureSelection();
                            focus && refocusKeepingCaret();
                          },
                          blurOnSubmit: true,
                          onChangeText: isShowChatTutorial
                            ? setMessage1
                            : setMessage,
                          value: isShowChatTutorial ? message1 : message,
                        },
                        default: {
                          onBlur: () => focusInputAtEnd(),
                          ...textInputProps,
                        },
                      })}
                      partTypes={[
                        {
                          isInsertSpaceAfterMention: true,
                          trigger: '@',
                          textStyle: { color: 'blue' },
                          renderSuggestions: renderMentionSuggestions,
                        },
                      ]}
                    />
                  </Box>
                </Box>
              </Box>
            </HoverableScrollView>
          )}

          {showRecording && (
            <VoiceRecord
              setShowRecording={(bool: boolean) => setShowRecording(bool)}
              voiceFileHandler={(voice) =>
                setLocalFiles(chatId!, [...localFiles, voice])
              }
            />
          )}

          {showOptionsBar &&
            !showRecording &&
            Platform.select({
              web: <Box marginBottom='xs'>{optionsBar}</Box>,
              default: (
                <ScrollView
                  keyboardShouldPersistTaps='always'
                  scrollEnabled={false}>
                  <Box marginBottom='m'>{optionsBar}</Box>
                </ScrollView>
              ),
            })}
        </Box>
      )}
      {showLeaveMessage &&
        (Platform.OS === 'web' ? (
          <Box
            borderTopWidth={1}
            borderColor='grey02'
            backgroundColor='grey02'
            paddingVertical='m'
            paddingHorizontal='m'
            alignItems='center'
            justifyContent='center'>
            <Text
              variant='metadataSecondary'
              color='onSurfaceSecondary'
              fontWeight='500'
              textAlign='center'>
              {isPendingRequest
                ? t('shared:noChatMemberMessage')
                : t('shared:noLongerMessage')}
            </Text>
          </Box>
        ) : (
          <Box marginBottom='m' borderTopWidth={1} borderColor='grey02'>
            <Text
              variant='metadataSecondary'
              color='onSurfaceSecondary'
              fontWeight='500'
              marginLeft='m'
              marginTop='m'>
              {isPendingRequest
                ? t('shared:noChatMemberMessage')
                : t('shared:noLongerMessage')}
            </Text>
          </Box>
        ))}
      {isBlocked && (
        <Box
          ref={ref}
          backgroundColor='white'
          borderTopStartRadius='m'
          borderTopEndRadius='m'
          borderTopWidth={1}
          borderRightWidth={1}
          borderLeftWidth={1}
          borderColor='grey02'
          zIndex={2}>
          <Text
            variant='labelSmall'
            color='onSurfaceSecondary'
            textAlign='center'
            paddingVertical='m'>
            {t('models:chat.blockmessage')}
          </Text>
        </Box>
      )}
    </Box>
  );
};
const styles = StyleSheet.create({
  input: {
    ...((Platform.OS === 'web' && {
      outlineStyle: 'none',
      maxHeight: 5 * theme.textVariants.body1.lineHeight,
    }) || {
      marginHorizontal: theme.spacing.xs,
      marginTop: -theme.spacing.xxs,
    }),
    flex: Platform.OS === 'web' ? null : 1,
    paddingVertical: Platform.OS === 'android' ? theme.spacing.xxs : undefined,
    textAlignVertical: 'center',
    ...theme.textVariants.body,
    color: theme.colors.textPrimary,
    fontSize: 14,
    fontFamily: theme.textVariants.body1.fontFamily,
    lineHeight: theme.textVariants.body1.lineHeight,
  },
  topShadow: {
    shadowColor: 'black',
    shadowOpacity: Platform.OS === 'android' ? 0.5 : 0.25,
    shadowOffset: { width: 0, height: 25 },
    shadowRadius: 20,
    elevation: 25,
  },
  suggestionBox: {
    position: 'absolute',
    bottom: `100%`,
    left: 0,
    right: 0,
    backgroundColor: 'transparent',
    maxHeight: Dimensions.get('window').height < 700 ? 200 : 250, // for iPhone SE
    shadowColor: Platform.OS === 'android' ? `rgba(0,0,0,0.5)` : 'black',
    shadowOpacity: 0.05,
    shadowOffset: { width: 0, height: 5 },
    shadowRadius: 25,
    elevation: 20,
    zIndex: 3,
  },
});

export default ChatForm;
